import React from "react";
import { AlertTriangle, Inbox, Loader2, RotateCw, ShieldOff } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/misc";

// A spinner with words for screen readers, for buttons that show only a spinner.
export const Busy = ({ label }) => (
  <>
    <Loader2 className="tw-size-4 tw-animate-spin" aria-hidden="true" />
    <span className="tw-sr-only">{label}</span>
  </>
);

export const NoAccess = () => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-dashed tw-bg-background tw-px-6 tw-py-14 tw-text-center">
    <div className="tw-flex tw-size-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted">
      <ShieldOff className="tw-size-5 tw-text-muted-foreground" aria-hidden="true" />
    </div>
    <strong className="tw-text-base tw-font-semibold tw-text-foreground">You don't have access</strong>
    <span className="tw-max-w-sm tw-text-sm tw-text-muted-foreground">
      The D2C dashboard is open to super admins and users with the d2c-ops permission.
    </span>
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, children }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-px-6 tw-py-10 tw-text-center">
    <div className="tw-mb-1 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted">
      <Icon className="tw-size-5 tw-text-muted-foreground" aria-hidden="true" />
    </div>
    {title && <div className="tw-text-sm tw-font-medium tw-text-foreground">{title}</div>}
    {children && <div className="tw-max-w-md tw-text-sm tw-text-muted-foreground">{children}</div>}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div
    className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-red-200 tw-bg-red-50 tw-px-4 tw-py-3 tw-text-sm tw-text-red-800"
    role="alert"
  >
    <span className="tw-flex tw-items-center tw-gap-2">
      <AlertTriangle className="tw-size-4 tw-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </span>
    {onRetry && (
      <Button size="sm" variant="outline" onClick={onRetry} className="tw-border-red-200 tw-text-red-800">
        <RotateCw aria-hidden="true" /> Retry
      </Button>
    )}
  </div>
);

export const SkeletonRows = ({ rows = 5 }) => (
  <div className="tw-space-y-2 tw-p-4" role="status">
    <span className="tw-sr-only">Loading...</span>
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} className="tw-h-8 tw-w-full" />
    ))}
  </div>
);

// Wraps one data area: a skeleton on first load, a message on error, an empty
// state, and the content otherwise. A 403 always reads as "no access".
const StateBlock = ({ loading, error, onRetry, isEmpty, emptyText, emptyIcon, skeleton, children }) => {
  if (error?.status === 403) return <NoAccess />;
  if (error) return <ErrorState message={error.message} onRetry={onRetry} />;
  if (loading && isEmpty) return skeleton || <SkeletonRows />;
  if (isEmpty) return <EmptyState icon={emptyIcon}>{emptyText || "Nothing to show for these filters."}</EmptyState>;
  return children;
};

export default StateBlock;
